import { useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { buildSceneCrossSectionElement, SceneCrossSections } from '@scene'
import { useTheme } from '@mui/material'
import { ThreeEvent } from '@react-three/fiber'
import { useControlStore } from '@editorStores'
import {
  useResultsSelection,
  useElementTypes,
  useGuidToElementIncludingRips,
  useHideElements,
  useModelClickListeners,
  useSceneElement,
} from '@editorHooks'
import { useSceneStore } from 'src/components/pages/Editor/stores/sceneStore'
import { useModelMeshStore } from 'src/components/scene/ModelMeshV2/state/store'
import type { DesignFormRootValues, DesignFormValues } from '../types'

const mapElementTypeForResultsClick = (
  elementType: ElementTypes | undefined,
): ResultElementType | 'inner_walls' | 'outer_walls' | undefined => {
  if (!elementType) return undefined
  if (elementType === 'rips') return 'rip'
  if (elementType === 'lintels') return 'lintel'
  if (elementType === 'standard_rips' || elementType === 'foundation') return undefined
  return elementType
}

const SyncSingleCrossSectionWithScene = ({
  index,
  onClick,
}: {
  index: number
  onClick: (event: ThreeEvent<MouseEvent>) => void
}) => {
  const theme = useTheme()

  const crossSection = useWatch<DesignFormRootValues>({
    name: `elements.${index}.crossSection`,
  }) as CrossSectionTimberOrCLTOrSteel | undefined
  const elementGuid = useWatch<DesignFormRootValues>({ name: `elements.${index}.elementGuid` })
  const guidToElement = useGuidToElementIncludingRips()
  const mappedColor = useSceneStore(
    useCallback(state => state.guidToColor[elementGuid], [elementGuid]),
  )

  const selectedGuids = useModelMeshStore(state => state.selectedGuids)
  const { selectedElements } = useResultsSelection()

  const allSelectedElements = useMemo(() => {
    return [...selectedElements, ...Array.from(selectedGuids.values()).flat()]
  }, [selectedElements, selectedGuids])

  const sceneElements = useMemo(
    () =>
      crossSection &&
      buildSceneCrossSectionElement(
        {
          crossSection: crossSection,
          elementGuid,
          color: mappedColor,
          outlinesColor: allSelectedElements.includes(elementGuid)
            ? (theme.scenePalette.highlightDark as string)
            : undefined,
        },
        guidToElement[elementGuid],
      ),
    [
      crossSection,
      elementGuid,
      guidToElement,
      mappedColor,
      allSelectedElements,
      theme.scenePalette.highlightDark,
    ],
  )

  useHideElements(crossSection ? elementGuid : undefined)

  const sceneElement = useMemo(
    () => ({
      enabled: !!crossSection,
      properties: { elements: sceneElements ? [sceneElements] : [], onClick },
      component: SceneCrossSections,
    }),
    [crossSection, onClick, sceneElements],
  )

  useSceneElement(sceneElement)

  return null
}

interface Props {
  disabled: boolean
}

const SyncCrossSectionWithScene = ({ disabled }: Props) => {
  const elements = useWatch<DesignFormRootValues>({ name: 'elements' }) as
    | DesignFormValues[]
    | undefined
  const onModelClick = useModelClickListeners()
  const elementTypes = useElementTypes()

  // In floorplan mode the 3D cross-section preview must not be rendered.
  const isFloorplanMode = useControlStore(state => state.floorplanState.storey) !== null

  const onClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      const guid = event.object.name
      const mappedElementType = mapElementTypeForResultsClick(elementTypes[guid])
      if (!mappedElementType) return

      event.object.userData.elementType = mappedElementType
      onModelClick(event)
    },
    [elementTypes, onModelClick],
  )

  if (disabled || isFloorplanMode) return null

  return (
    <>
      {elements?.map((element, index) => (
        <SyncSingleCrossSectionWithScene
          key={element.elementGuid}
          index={index}
          onClick={onClick}
        />
      ))}
    </>
  )
}

export default SyncCrossSectionWithScene
